import React, { useContext } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import Content from "components/sidebar/components/Content";
import Links from "components/sidebar/components/Links";
import { AuthContext } from "context/index.js";
import { canAccessRoute } from "utils/modulePermissions";
import PropTypes from "prop-types";
import versionData from "utils/version.json";

function Sidebar(props) {
  const { routes, isCollapsed, isMobileOpen, onMobileClose } = props;

  const variantChange = "0.3s ease";
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.08)",
    "unset"
  );
  const sidebarBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("gray.500", "gray.400");
  const sectionBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const user = useContext(AuthContext)?.user;

  // Las rutas `pinned` (Configuraciones) quedan fijas al pie, encima de la versión, y no
  // se desplazan con la lista. Si el usuario no puede ver ninguna, no se pinta el bloque.
  const hayAncladas = (routes || []).some(
    (route) => route.pinned && route.show && canAccessRoute(user, route)
  );
  const pieAnclado = (collapsed, onNavigate) =>
    hayAncladas && (
      <Box
        borderTopWidth="1px"
        borderTopStyle="solid"
        borderTopColor={sectionBorder}
        py="8px"
        px={collapsed ? "8px" : "16px"}
        bg={sidebarBg}
        flexShrink="0"
      >
        <Box ps="4px">
          <Links routes={routes} pinned isCollapsed={collapsed} onNavigate={onNavigate} />
        </Box>
      </Box>
    );

  return (
    <Box position="fixed" h="100vh" zIndex="1000">
      <Box display={{ base: "none", lg: "block" }} h="100vh">
        <Box
          bg={sidebarBg}
          transition={`width ${variantChange}`}
          w={{ base: "100%", xl: isCollapsed ? "100px" : "270px" }}
          h="100vh"
          m="0px"
          overflowX="hidden"
          overflowY="hidden"
          boxShadow={shadow}
          display="flex"
          flexDirection="column"
        >
          <Box
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            sx={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(160, 174, 192, 0.8) transparent",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(160, 174, 192, 0.8)",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
            }}
          >
            <Content routes={routes} isCollapsed={isCollapsed} />
          </Box>

          {pieAnclado(isCollapsed)}

          {!isCollapsed && (
            <Box
              borderTopWidth="1px"
              borderTopStyle="solid"
              borderTopColor={sectionBorder}
              py="12px"
              px="16px"
              bg={sidebarBg}
              textAlign="center"
              flexShrink="0"
            >
              <Text fontSize="xs" color={textColor} fontWeight="500">
                Version {versionData.version}
              </Text>
            </Box>
          )}
        </Box>
      </Box>

      <Drawer isOpen={Boolean(isMobileOpen)} placement="left" onClose={onMobileClose}>
        <DrawerOverlay />
        <DrawerContent w="min(270px, 85vw)" maxW="85vw" bg={sidebarBg}>
          <DrawerBody p="0">
            <Box h="100vh" display="flex" flexDirection="column" overflow="hidden">
              <Box flex="1" overflowY="auto" overflowX="hidden">
                <Content routes={routes} isCollapsed={false} onNavigate={onMobileClose} />
              </Box>
              {pieAnclado(false, onMobileClose)}
              <Box
                borderTopWidth="1px"
                borderTopStyle="solid"
                borderTopColor={sectionBorder}
                py="12px"
                px="16px"
                bg={sidebarBg}
                textAlign="center"
                flexShrink="0"
              >
                <Text fontSize="xs" color={textColor} fontWeight="500">
                  Version {versionData.version}
                </Text>
              </Box>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

Sidebar.propTypes = {
  logoText: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.string,
  isMobileOpen: PropTypes.bool,
  onMobileClose: PropTypes.func,
};

export default Sidebar;
